import { useEffect, useRef, forwardRef } from 'react';

interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
  fadeDir: number;
  twinkleSpeed: number;
  hue: number;
  orbitRadius: number;
  orbitAngle: number;
  orbitSpeed: number;
}

interface GoldenParticlesProps {
  count?: number;
  className?: string;
}

export const GoldenParticles = forwardRef<HTMLCanvasElement, GoldenParticlesProps>(function GoldenParticles({ count = 35, className = '' }, _ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let particles: Particle[] = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    // Color palette: gold, violet, silver-blue, rose
    const hues = [42, 270, 220, 330, 42, 42, 260];

    const createParticle = (): Particle => {
      const hue = hues[Math.floor(Math.random() * hues.length)];
      return {
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        size: Math.random() * 2.2 + 0.3,
        speedX: (Math.random() - 0.5) * 0.15,
        speedY: -Math.random() * 0.3 - 0.05,
        opacity: Math.random() * 0.5,
        fadeDir: 1,
        twinkleSpeed: Math.random() * 0.01 + 0.003,
        hue,
        orbitRadius: Math.random() * 18 + 4,
        orbitAngle: Math.random() * Math.PI * 2,
        orbitSpeed: (Math.random() - 0.5) * 0.012,
      };
    };

    const init = () => {
      resize();
      particles = Array.from({ length: count }, createParticle);
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      for (const p of particles) {
        // Twinkle
        p.opacity += p.twinkleSpeed * p.fadeDir;
        if (p.opacity >= 0.55) p.fadeDir = -1;
        if (p.opacity <= 0.03) p.fadeDir = 1;

        // Orbit
        p.orbitAngle += p.orbitSpeed;
        const ox = Math.cos(p.orbitAngle) * p.orbitRadius;
        const oy = Math.sin(p.orbitAngle) * p.orbitRadius * 0.5;

        // Drift
        p.x += p.speedX;
        p.y += p.speedY;

        const drawX = p.x + ox;
        const drawY = p.y + oy;

        // Wrap
        if (p.y < -20) { p.y = canvas.offsetHeight + 10; p.x = Math.random() * canvas.offsetWidth; }
        if (p.x < -20) p.x = canvas.offsetWidth + 10;
        if (p.x > canvas.offsetWidth + 20) p.x = -10;

        const sat = p.hue === 42 ? 60 : 45;
        const lit = p.hue === 42 ? 55 : 60;

        // Draw glow
        const gradient = ctx.createRadialGradient(drawX, drawY, 0, drawX, drawY, p.size * 4);
        gradient.addColorStop(0, `hsla(${p.hue}, ${sat}%, ${lit}%, ${p.opacity})`);
        gradient.addColorStop(0.4, `hsla(${p.hue}, ${sat + 10}%, ${lit - 5}%, ${p.opacity * 0.35})`);
        gradient.addColorStop(1, `hsla(${p.hue}, ${sat}%, ${lit}%, 0)`);

        ctx.beginPath();
        ctx.arc(drawX, drawY, p.size * 4, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Draw core
        ctx.beginPath();
        ctx.arc(drawX, drawY, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue}, ${sat + 15}%, ${lit + 10}%, ${p.opacity})`;
        ctx.fill();
      }

      animationId = requestAnimationFrame(draw);
    };

    init();
    draw();

    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, [count]);

  return (
    <canvas
      ref={canvasRef}
      className={`pointer-events-none absolute inset-0 z-10 ${className}`}
      aria-hidden="true"
    />
  );
});

GoldenParticles.displayName = 'GoldenParticles';
